/*
 * Steals Wishlist — Storage Layer
 * Abstracts localStorage (guest) vs Storefront API (logged-in).
 * Syncs with app via customer metafield custom.wishlist.
 */

const StealsWishlist = (() => {
  const LOCAL_KEY = 'steals-wishlist';
  const METAFIELD_NAMESPACE = 'custom';
  const METAFIELD_KEY = 'wishlist';

  let _cache = [];
  let _initialized = false;
  let _isLoggedIn = false;
  let _customerId = null;
  let _storefrontToken = '';
  let _shopUrl = '';

  function _localGet() {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_KEY)) || [];
    } catch {
      return [];
    }
  }

  function _localSet(ids) {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(ids));
  }

  function _localClear() {
    localStorage.removeItem(LOCAL_KEY);
  }

  function _gqlFetch(query, variables = {}) {
    return fetch(`${_shopUrl}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': _storefrontToken,
      },
      body: JSON.stringify({ query, variables }),
    }).then(r => r.json());
  }

  async function _apiGet() {
    if (!_customerId) return [];
    const query = `
      query getWishlist($customerId: ID!) {
        customer(id: $customerId) {
          metafield(namespace: "${METAFIELD_NAMESPACE}", key: "${METAFIELD_KEY}") {
            value
          }
        }
      }
    `;
    try {
      const gid = `gid://shopify/Customer/${_customerId}`;
      const res = await _gqlFetch(query, { customerId: gid });
      const val = res?.data?.customer?.metafield?.value;
      if (!val) return [];
      const gids = JSON.parse(val);
      return gids.map(gid => gid.replace('gid://shopify/Product/', ''));
    } catch {
      return [];
    }
  }

  async function _apiSet(ids) {
    if (!_customerId) return;
    const gids = ids.map(id => `gid://shopify/Product/${id}`);
    const mutation = `
      mutation updateWishlist($metafields: [MetafieldsSetInput!]!) {
        metafieldsSet(metafields: $metafields) {
          metafields { key }
          userErrors { field message }
        }
      }
    `;
    try {
      await _gqlFetch(mutation, {
        metafields: [{
          ownerId: `gid://shopify/Customer/${_customerId}`,
          namespace: METAFIELD_NAMESPACE,
          key: METAFIELD_KEY,
          value: JSON.stringify(gids),
          type: 'list.product_reference',
        }],
      });
    } catch (err) {
      console.error('[StealsWishlist] API write failed, falling back to localStorage', err);
      _localSet(ids);
    }
  }

  async function _persist(ids) {
    if (_isLoggedIn) {
      await _apiSet(ids);
    } else {
      _localSet(ids);
    }
  }

  async function _load() {
    if (_isLoggedIn) {
      return await _apiGet();
    }
    return _localGet();
  }

  async function _mergeIfNeeded() {
    if (!_isLoggedIn) return;
    const localIds = _localGet();
    if (localIds.length === 0) return;
    const apiIds = await _apiGet();
    const merged = [...new Set([...apiIds, ...localIds])];
    await _apiSet(merged);
    _localClear();
    _cache = merged;
    _publish();
  }

  function _publish(productId, action) {
    if (typeof publish === 'function') {
      publish('wishlist:changed', {
        productId: productId || null,
        action: action || null,
        wishlist: [..._cache],
      });
    }
  }

  async function init() {
    if (_initialized) return;
    _shopUrl = window.shopUrl || window.location.origin;
    _customerId = window.theme?.customerId || null;
    _storefrontToken = window.theme?.storefrontToken || '';
    _isLoggedIn = !!_customerId && !!_storefrontToken;
    _cache = await _load();
    _initialized = true;
    await _mergeIfNeeded();
    _publish();
  }

  async function getAll() {
    if (!_initialized) await init();
    return [..._cache];
  }

  async function toggle(productId) {
    if (!_initialized) await init();
    const id = String(productId);
    const index = _cache.indexOf(id);
    const added = index === -1;
    if (added) {
      _cache.push(id);
    } else {
      _cache.splice(index, 1);
    }
    _publish(id, added ? 'added' : 'removed');
    await _persist(_cache);
    return added;
  }

  function has(productId) {
    return _cache.includes(String(productId));
  }

  function count() {
    return _cache.length;
  }

  return { init, getAll, toggle, has, count };
})();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => StealsWishlist.init());
} else {
  StealsWishlist.init();
}
